import { createContext, useContext, useEffect, useRef, useState } from "react";
import _ from "lodash";
import {
  JsonParam,
  ArrayParam,
  StringParam,
  NumberParam,
  BooleanParam,
  useQueryParam
} from "use-query-params";
import {
  Room,
  Floor,
  floors,
  ParkingSpot,
  RealEstateContact,
  GenericPropertyDetails,
  ListingsRealtorBlacklist,
  FavoriteListingsOfContact,
  genericPropertyDetailsKeys,
  ListingsRealtorSavedList,
  ListingOrigin,
  ListingOrigins,
  ListingListedFor,
  listingListedFor as listingListedForValues,
  getContactCustomerTypes,
  SYSTEM_CONTACT_ORIGINS
} from "./../@types/sharedSchema";
import { fetchListings } from "components/_dashboard/listingsMatch/algoliaClient";
import {
  asFiniteNumber,
  asPositiveNumber
} from "components/_dashboard/contacts/detail/tabs/matches/contactMatchFilters";
import { useAlgoliaIndex } from "./AlgoliaClientContext";
import { isPgSearchEnabled } from "utils/listingsSearchApi";
import { fetchListingsByIds } from "utils/listings/fetchListings";
import { tickedAmenities } from "utils/contacts/amenityLabels";
import type { DateRangeFilterValue } from "components/_dashboard/common/DateRangeFilter";
import { ProcessedListingAddress, ProcessedListingData } from "./../@types/sharedSchema";
import { readContactOnce } from "utils/contacts/readContactDocument";
import type { ContactGetter } from "utils/contacts/readContactDocument";
import useAuth from "hooks/useAuth";
import useWebotServer from "hooks/useWebotServer";
import useContactWrites from "hooks/contacts/useContactWrites";
import { useLiveContact } from "hooks/useLiveContact";
import {
  addListingToFavorites,
  addListingsToFavorites,
  removeListingFromFavorites,
  removeListingsFromFavorites,
  addListingsListToFavorites,
  createFavoriteIfNotExists,
  getFavoritesListingsQuery,
  addListingToRealtorBlackList,
  getUserFavoritesListingsQuery,
  removeListingsListFromFavorites,
  getRealtorListingsBlacklistQuery,
  removeListingFromRealtorBlackList
} from "utils/Haim/firestoreUtils";
import { pickMinisitePage } from "utils/minisite/minisitePage";
import {
  addListingsToUserList,
  removeListingFromUserList,
  createUserList,
  getUserLists,
  deleteUserList,
  editUserListName
} from "utils/Haim/listingListsUtils";
import { useSnackbar } from "notistack5";
import { useTranslation } from "react-i18next";
import { useAgencyNames } from "hooks/useAgencyNames";
import { useSellerNames } from "hooks/useSellerNames";
import { usePropertyTypes } from "hooks/usePropertyTypes";
import { usePostHog } from "posthog-js/react";
import useFirestore from "hooks/useFirestore";
import { toPersistedLocations } from "utils/locations/locationGroups";
import type { PropsWithChildren } from "react";
import type { AlgoliaHit } from "instantsearch.js";
import {
  healthCrumb,
  healthSearchEnded,
  healthSearchStarted,
  noteHealthRender
} from "components/_dashboard/listingsMatch/listingsMatchHealth";

export const hiddenBooleanPropertiesDetailsKeys = [
  "hasHousingUnit", // Yad2 has no structured boolean — only free-text mentions of "יחידת הורים"
  "hasGarden" // Yad2 exposes garden size (squareMeterGarden) but not a boolean — not wired through yet

  // hasMamak / hasMiklat were hidden here pending Yad2 data ("open this after 1 June 2026").
  // Measured 2026-09-22 over the 840,306-row mirror (adHocOperations/count-shelter-amenities.ts):
  // hasMiklat 6.46%, hasMamak 0.78%, and 53,250 listings (6.34%) carry one of them WITHOUT a ממ"ד
  // — i.e. exactly the listings a "ממ\"ד or מקלט" buyer was being denied. They are back, and they
  // are safe to offer because they no longer AND: the server ORs the מרחב מוגן group
  // (`PROTECTED_SPACE_KEYS` / the `amenities && …` leg), so ticking a second one can only ADD
  // listings. Ticking all three used to leave 1,082 of 840,306 (0.13%).
];

type BooleanPropertiesDetails = Partial<GenericPropertyDetails>;

export type SearchState = {
  pickedContact?: RealEstateContact | null;
  searchTerm: string;
  locations: ProcessedListingAddress[];
  listingOrigins: ListingOrigin[];
  minRooms: Room | undefined;
  maxRooms: Room | undefined;
  minPrice: number | undefined;
  maxPrice: number | undefined;
  /**
   * Whether a budget also admits listings with NO price (Yad2's "מחיר לא צוין",
   * stored as price 0). Off by default, and meaningless without a budget — it
   * widens the price filter, it is not a filter of its own. Not a query param, so
   * a shared link carries the default; same treatment as `publishedDateRange`.
   */
  includeListingsWithoutPrice?: boolean;
  minFloors: Floor | undefined;
  maxFloors: Floor | undefined;
  minParkingSpots: ParkingSpot;
  maxParkingSpots: ParkingSpot;
  /**
   * Built area (שטח בנוי) in m². Free numbers, not a scale — a size is typed, not stepped.
   * `undefined` on either side is an open bound; 0 is never stored (it means "not set").
   * A listing that states no size matches neither bound and drops out of a sized search.
   */
  minSquareMeter?: number;
  maxSquareMeter?: number;
  agencyNames: string[];
  sellerNames: string[];
  propertyTypes: string[];
  adTypes: string[];
  listingListedFor: ListingListedFor;
  booleanPropertiesDetails?: BooleanPropertiesDetails;
  hidePotentiallyRemovedListings: boolean;
  isAssetExclusive: boolean;
  onlyPriceChanged: boolean;
  /** Restrict results to a published-date window (preset or custom range). */
  publishedDateRange?: DateRangeFilterValue;
};

const defaultSearchState: SearchState = {
  pickedContact: undefined,
  searchTerm: "",
  locations: [],
  listingOrigins: [...ListingOrigins],
  adTypes: [],
  minRooms: undefined,
  maxRooms: undefined,
  minPrice: undefined,
  maxPrice: undefined,
  includeListingsWithoutPrice: false,
  minFloors: undefined,
  maxFloors: undefined,
  minParkingSpots: undefined,
  maxParkingSpots: undefined,
  minSquareMeter: undefined,
  maxSquareMeter: undefined,
  agencyNames: [],
  sellerNames: [],
  propertyTypes: [],
  listingListedFor: "sale",
  // An empty bag, never absent: the filters drawer renders the amenity block (the
  // מרחב מוגן group included) from this object and renders nothing at all without one,
  // so "clear" and un-picking a contact used to remove the whole block until a reload.
  booleanPropertiesDetails: {},
  hidePotentiallyRemovedListings: true,
  isAssetExclusive: false,
  onlyPriceChanged: false
};

const isSearchStateDirty = (searchState: Partial<SearchState>) => {
  console.log("searchState.listingOrigins", searchState.listingOrigins);
  console.log("ListingOrigins", ListingOrigins);
  return (
    searchState.searchTerm ||
    searchState.locations?.length > 0 ||
    (searchState.listingOrigins &&
      !_.isEqual([...searchState.listingOrigins], [...ListingOrigins])) ||
    searchState.minRooms > 0 ||
    searchState.maxRooms > 0 ||
    searchState.minPrice > 0 ||
    searchState.maxPrice > 0 ||
    searchState.minFloors != null ||
    searchState.maxFloors != null ||
    searchState.minParkingSpots > 0 ||
    searchState.maxParkingSpots > 0 ||
    searchState.minSquareMeter > 0 ||
    searchState.maxSquareMeter > 0 ||
    searchState.propertyTypes?.length > 0 ||
    searchState.agencyNames?.length > 0 ||
    searchState.sellerNames?.length > 0 ||
    searchState.adTypes?.length > 0 ||
    searchState.listingListedFor !== defaultSearchState.listingListedFor ||
    searchState.hidePotentiallyRemovedListings ||
    searchState.isAssetExclusive ||
    searchState.onlyPriceChanged ||
    (searchState.booleanPropertiesDetails &&
      Object.keys(searchState.booleanPropertiesDetails).filter(
        (k) => !!searchState.booleanPropertiesDetails[k]
      ).length > 0)
  );
};

const budgetBufferPercentage = 0.1;

/** The top-level keys two objects disagree on - names only, for the page's health breadcrumbs. */
const changedKeys = (next: unknown, previous: unknown): string => {
  const a = (next ?? {}) as Record<string, unknown>;
  const b = (previous ?? {}) as Record<string, unknown>;
  return _.union(Object.keys(a), Object.keys(b))
    .filter((key) => !_.isEqual(a[key], b[key]))
    .slice(0, 12)
    .join(",");
};

// Helper function to find the closest valid floor value.
// Type-hardened (2026-08-19): stored contact values have carried strings ("3") and
// empty strings in the numeric fields; "" used to coerce to 0 in the arithmetic and
// seed a forced 0-0 floor filter. Same rule as contactMatchFilters.asFiniteNumber.
const getClosestValidFloor = (rawValue: number | null | undefined): Floor | undefined => {
  const value = asFiniteNumber(rawValue);
  if (value === undefined) return undefined;
  // If the value is already a valid Floor, return it
  if (floors.includes(value as any)) return value as Floor;
  // Find the closest valid floor value
  const closestFloor = floors.reduce((prev, curr) =>
    Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
  );
  return closestFloor;
};

const fetchContact = async (
  contactsGet: ContactGetter,
  user,
  contactNumber: string
): Promise<RealEstateContact | null> => {
  if (!user?.email || !contactNumber) {
    return null;
  }

  contactNumber = contactNumber.replaceAll(" ", "");
  contactNumber = contactNumber.replaceAll("-", "");

  if (!contactNumber || contactNumber === "") {
    return null;
  }

  if (!contactNumber.startsWith("+")) {
    contactNumber = `+${contactNumber}`;
  }

  try {
    // From where this browser reads contacts (`readContactOnce`): the search below is seeded
    // from this contact's criteria, so in a shared office it must be the office's record,
    // never the member's frozen copy from before the merge.
    const contact = await readContactOnce(contactsGet, user.email, { contactId: contactNumber });
    return contact ?? ({ id: contactNumber } as RealEstateContact);
  } catch (error) {
    console.error("Error fetching contact: ", error);
    return null;
  }
};

const getNewStateSearchFromContactFields = (
  contact: RealEstateContact | undefined
): SearchState => {
  console.log("Selected contact", contact);
  const newSearchState = _.cloneDeep(defaultSearchState);
  if (!contact) {
    console.log("The contact is null");
    newSearchState.pickedContact = null;
    return newSearchState;
  }

  // (Was: a "temp fix" that copied `contact.id` into `callerPhoneNumber` because the
  // contact dialog read that field and the document does not always have it. The readers
  // now take the document id directly, which is the identity, so there is nothing to
  // patch here.)

  const {
    customerBudget,
    roomsMax,
    roomsMin,
    floorsMax,
    floorsMin,
    interestedInLocations,
    interestedInPropertyTypes
  } = contact ?? {};
  newSearchState.pickedContact = contact;

  // `interestedInLocations` is the only source. There used to be a fallback that split the
  // deprecated free-text `contact.neighborhood` on commas and turned each piece into
  // `{ neighborhood: n }` — a location with NO CITY, which is precisely the shape
  // `location-ssot` refuses to construct: `רמת אשכול` alone names a real neighbourhood in
  // חיפה, ירושלים AND אשקלון, so searching on it means searching all three. An empty
  // location set is the honest answer for a contact that has no areas recorded.
  newSearchState.locations = interestedInLocations?.length ? interestedInLocations : [];

  if (interestedInPropertyTypes && interestedInPropertyTypes.length > 0) {
    newSearchState.propertyTypes = interestedInPropertyTypes;
  }

  newSearchState.minRooms = asFiniteNumber(roomsMin) as Room | undefined;
  newSearchState.maxRooms = asFiniteNumber(roomsMax) as Room | undefined;
  newSearchState.minFloors = getClosestValidFloor(floorsMin);
  newSearchState.maxFloors = getClosestValidFloor(floorsMax);
  // The built area EXACTLY as the card states it (no widening, unlike a lone budget): the chip
  // then reads what the realtor typed on the contact, and save-search-to-contact is a round trip.
  newSearchState.minSquareMeter = asPositiveNumber(contact.squareMeterMin);
  newSearchState.maxSquareMeter = asPositiveNumber(contact.squareMeterMax);
  // Multi-type: rent search only when the contact is a tenant and NOT also a buyer
  // (matches the server-side suggest_listings inference).
  const contactCustomerTypes = getContactCustomerTypes(contact);
  const isRenter = contactCustomerTypes.includes("שוכר") && !contactCustomerTypes.includes("קונה");
  const budget = isRenter ? contact.customerBudgetRent || customerBudget : customerBudget;
  // The stored budget is semantically the MAX. When the explicit lower bound
  // (customerBudget(Rent)Min) is stored, the range seeds EXACTLY — the ±10%
  // buffer is only a heuristic for a lone budget value. Parity with the contact
  // page's buildFiltersFromContact, and what makes save-search-to-contact a
  // round trip instead of drifting on every save.
  // `hasBudgetMin`, not truthiness: a stored floor of ZERO is a real bound — "everything
  // under the budget" — and is a different instruction from a lone budget, which the else
  // branches spread ±10% around. Read as truthy/falsy, a 0 floor seeded 0.9x–1.1x the
  // budget instead, i.e. a search that excludes exactly the cheap listings it asked for.
  const budgetMinRaw = isRenter ? contact.customerBudgetRentMin : contact.customerBudgetMin;
  const budgetMin = asFiniteNumber(budgetMinRaw);
  const hasBudgetMin = budgetMin !== undefined && budgetMin >= 0;
  newSearchState.minPrice = budget
    ? hasBudgetMin
      ? budgetMin
      : budget * (1 - budgetBufferPercentage)
    : undefined;
  newSearchState.maxPrice = budget
    ? hasBudgetMin
      ? budget
      : budget * (1 + budgetBufferPercentage)
    : undefined;
  // A floor of EXACTLY 0 means "no floor", so listings with no price belong in the
  // answer rather than being dropped for a price they do not state. Same rule as the
  // contact page's buildFiltersFromContact — keep the two in step. Only an explicit 0
  // qualifies; the ±10% heuristic above cannot produce one.
  newSearchState.includeListingsWithoutPrice = Boolean(budget) && budgetMin === 0;

  // The wishes the contact STATES, read strictly — never the fields copied as stored. An old
  // import left the text "TRUE" / "FALSE" in these fields on some contacts, and copied as it
  // was that text became a filter the server refuses: the search came back empty. See
  // `tickedAmenities`.
  newSearchState.booleanPropertiesDetails = tickedAmenities(
    contact as unknown as Record<string, unknown>,
    genericPropertyDetailsKeys.filter((p) => !hiddenBooleanPropertiesDetailsKeys.includes(p))
  ) as BooleanPropertiesDetails;
  newSearchState.listingListedFor = isRenter ? "rent" : "sale";
  return newSearchState;
};

type ListingsMatchContextType = {
  searchState: SearchState;
  draftSearchState: SearchState;
  setDraftSearchState: (s: Partial<SearchState>) => void;
  applyDraftSearchState: () => void;
  updateSearchState: (s: Partial<SearchState>) => void;
  updatePickedContact: (contact: RealEstateContact | undefined) => void;
  clearSearchState: () => void;
  clearDraftSearchState: () => void;
  availableAgencyNames: string[];
  availableSellerNames: string[];
  availablePropertyTypes: string[];
  isSearchInProgress: boolean;
  isBootstrapInProgress: boolean;
  hits?: AlgoliaHit<ProcessedListingData>[];
  totalHits?: number;
  isDirty: boolean;
  favoriteListingsOfPickedContact: FavoriteListingsOfContact;
  favoriteUserListingsOfPickedContact: FavoriteListingsOfContact[];
  blacklistedListings: ListingsRealtorBlacklist;
  listingslists: ListingsRealtorSavedList[];
  listIdToListingsData: Record<string, AlgoliaHit<ProcessedListingData>[]>;
  saveSearchStateToPickedContact: () => Promise<void>;
  addListingToContactsFavorites: (listingId: string) => Promise<void>;
  addListingsToContactsFavorites: (listingIds: string[]) => Promise<void>;
  removeListingFromContactsFavorites: (listingId: string) => Promise<void>;
  removeListingsFromContactsFavorites: (listingIds: string[]) => Promise<void>;
  addListingsListToContactsFavorites: (list: ListingsRealtorSavedList) => Promise<void>;
  removeListingsListFromContactsFavorites: (list: ListingsRealtorSavedList) => Promise<void>;
  addListingToBlackList: (realtorId: string, listingId: string) => Promise<void>;
  removeListingFromBlackList: (realtorId: string, listingId: string) => Promise<void>;
  addListingsList: (name: string) => Promise<void>;
  deleteListingsList: (listId: string) => Promise<void>;
  editListingsListName: (listId: string, name: string) => Promise<void>;
  addListingsToListingsList: (listId: string, listingIds: string[]) => Promise<void>;
  removeListingFromListingsList: (listId: string, listingId: string) => Promise<void>;
};

export const ListingsMatchContext = createContext<ListingsMatchContextType | null>(null);

export const contactQueryParamKey = "contact";
export const contactNumberQueryParamKey = "cn"; // Contact number in international format
export const searchQueryParamKey = "search";
export const locationsQueryParamKey = "locations";
export const listingOriginQueryParamKey = "listingOrigin";
export const minRoomQueryParamKey = "minRooms";
export const maxRoomQueryParamKey = "maxRooms";
export const minPriceQueryParamKey = "minPrice";
export const maxPriceQueryParamKey = "maxPrice";
export const minParkingSpotsQueryParamKey = "minParkingSpots";
export const maxParkingSpotsQueryParamKey = "maxParkingSpots";
export const agencyNamesQueryParamKey = "agencyNames";
export const sellerNamesQueryParamKey = "sellerNames";
export const propertyTypesQueryParamKey = "propertyTypes";
export const adTypesQueryParamKey = "adTypes";
export const booleanPropertiesDetailsParamKey = "booleanPropertiesDetails";
export const minFloorsQueryParamKey = "minFloors";
export const maxFloorsQueryParamKey = "maxFloors";
export const minSquareMeterQueryParamKey = "minSquareMeter";
export const maxSquareMeterQueryParamKey = "maxSquareMeter";
export const hidePotentiallyRemovedListingsQueryParamKey = "hidePotentiallyRemovedListings";
export const isAssetExclusiveQueryParamKey = "isAssetExclusive";
export const onlyPriceChangedQueryParamKey = "onlyPriceChanged";
export const listingListedForQueryParamKey = "listingListedFor";

const getSearchStatefromQueryParams = (
  contactParam: RealEstateContact | null,
  searchParam: string,
  locationsParam: ProcessedListingAddress[],
  listingOriginsParam: ListingOrigin[],
  minRoomsParam: string,
  maxRoomsParam: string,
  minPriceParam: number,
  maxPriceParam: number,
  minParkingSpotsParam: string,
  maxParkingSpotsParam: string,
  agencyNamesParam: string[],
  sellerNamesParam: string[],
  propertyTypesParam: string[],
  adTypesParam: string[],
  booleanPropertiesDetailsParam: BooleanPropertiesDetails,
  minFloorsParam: string,
  maxFloorsParam: string,
  hidePotentiallyRemovedListingsParam: boolean,
  isAssetExclusiveParam: boolean,
  onlyPriceChangedParam: boolean,
  listingListedForParam: string,
  minSquareMeterParam: number | null,
  maxSquareMeterParam: number | null
): SearchState => {
  const pickedContact = contactParam ?? undefined;
  const searchTerm = searchParam ?? "";
  const locations = (locationsParam as ProcessedListingAddress[]) ?? [];
  const listingOrigins = (listingOriginsParam as ListingOrigin[]) ?? [...ListingOrigins];
  const minRooms = parseFloat(minRoomsParam) as unknown as Room | undefined;
  const maxRooms = parseFloat(maxRoomsParam) as unknown as Room | undefined;
  const minPrice = minPriceParam;
  const maxPrice = maxPriceParam;
  const minFloors = parseFloat(minFloorsParam) as unknown as Floor | undefined;
  const maxFloors = parseFloat(maxFloorsParam) as unknown as Floor | undefined;
  const agencyNames = (agencyNamesParam as string[]) ?? [];
  const sellerNames = (sellerNamesParam as string[]) ?? [];
  const propertyTypes = (propertyTypesParam as string[]) ?? [];
  const adTypes = (adTypesParam as string[]) ?? [];
  const listingListedFor = (listingListedForParam ?? "sale") as ListingListedFor;
  // An address can be older than the build reading it — a bookmark, the browser's history, a
  // link somebody sent — and it is JSON, so it can say anything. Only a real `true` is a filter.
  const booleanPropertiesDetails = tickedAmenities(
    booleanPropertiesDetailsParam as unknown as Record<string, unknown>
  ) as BooleanPropertiesDetails;
  const minParkingSpots = parseFloat(minParkingSpotsParam) as ParkingSpot;
  const maxParkingSpots = parseFloat(maxParkingSpotsParam) as ParkingSpot;
  const hidePotentiallyRemovedListings = hidePotentiallyRemovedListingsParam;
  const isAssetExclusive = isAssetExclusiveParam;
  const onlyPriceChanged = onlyPriceChangedParam;
  return {
    pickedContact,
    searchTerm,
    locations,
    listingOrigins,
    minRooms: isNaN(minRooms) ? null : minRooms,
    maxRooms: isNaN(maxRooms) ? null : maxRooms,
    minPrice,
    maxPrice,
    minFloors: isNaN(minFloors) ? null : minFloors,
    maxFloors: isNaN(maxFloors) ? null : maxFloors,
    minParkingSpots: isNaN(minParkingSpots) ? null : minParkingSpots,
    maxParkingSpots: isNaN(maxParkingSpots) ? null : maxParkingSpots,
    minSquareMeter: asPositiveNumber(minSquareMeterParam),
    maxSquareMeter: asPositiveNumber(maxSquareMeterParam),
    agencyNames,
    sellerNames,
    propertyTypes,
    adTypes,
    listingListedFor,
    booleanPropertiesDetails,
    hidePotentiallyRemovedListings,
    isAssetExclusive,
    onlyPriceChanged
  };
};

export function ListingsMatchContextProvider({ children }: PropsWithChildren<any>) {
  noteHealthRender("provider");
  const { user } = useAuth();
  const { saveContact } = useContactWrites();
  const { contactsGet } = useWebotServer();
  const posthog = usePostHog();

  const { isLoadingOrganization } = useFirestore();
  const algoliaIndex = useAlgoliaIndex();

  // url query params
  const [contactNumberParam, setContactNumberParam] = useQueryParam(
    contactNumberQueryParamKey,
    StringParam
  );
  const [ContactParam, setContactParam] = useQueryParam(contactQueryParamKey, JsonParam);
  const [searchParam, setSearchParam] = useQueryParam(searchQueryParamKey, StringParam);
  const [locationsParam, setLocationsParam] = useQueryParam(locationsQueryParamKey, JsonParam);
  const [listingOriginsParam, setListingOriginsParam] = useQueryParam(
    listingOriginQueryParamKey,
    ArrayParam
  );
  const [minRoomsParam, setMinRoomsParam] = useQueryParam(minRoomQueryParamKey, StringParam);
  const [maxRoomsParam, setMaxRoomsParam] = useQueryParam(maxRoomQueryParamKey, StringParam);
  const [minPriceParam, setMinPriceParam] = useQueryParam(minPriceQueryParamKey, NumberParam);
  const [maxPriceParam, setMaxPriceParam] = useQueryParam(maxPriceQueryParamKey, NumberParam);
  const [agencyNamesParam, setAgencyNamesParam] = useQueryParam(
    agencyNamesQueryParamKey,
    ArrayParam
  );
  const [sellerNamesParam, setSellerNamesParam] = useQueryParam(
    sellerNamesQueryParamKey,
    ArrayParam
  );
  const [propertyTypesParam, setPropertyTypesParam] = useQueryParam(
    propertyTypesQueryParamKey,
    ArrayParam
  );

  const [adTypesParam, setAdTypesParam] = useQueryParam(adTypesQueryParamKey, ArrayParam);
  const [listingListedForParam, setListingListedForParam] = useQueryParam(
    listingListedForQueryParamKey,
    StringParam
  );
  const [booleanPropertiesDetailsParam, setBooleanPropertiesDetailsParam] = useQueryParam(
    booleanPropertiesDetailsParamKey,
    JsonParam
  );
  const [minFloorsParam, setMinFloorsParam] = useQueryParam(minFloorsQueryParamKey, StringParam);
  const [maxFloorsParam, setMaxFloorsParam] = useQueryParam(maxFloorsQueryParamKey, StringParam);
  const [minSquareMeterParam, setMinSquareMeterParam] = useQueryParam(
    minSquareMeterQueryParamKey,
    NumberParam
  );
  const [maxSquareMeterParam, setMaxSquareMeterParam] = useQueryParam(
    maxSquareMeterQueryParamKey,
    NumberParam
  );

  const [minParkingSpotsParam, setMinParkingSpotsParam] = useQueryParam(
    minParkingSpotsQueryParamKey,
    StringParam
  );
  const [maxParkingSpotsParam, setMaxParkingSpotsParam] = useQueryParam(
    maxParkingSpotsQueryParamKey,
    StringParam
  );

  const [hidePotentiallyRemovedListingsParam, setHidePotentiallyRemovedListingsParam] =
    useQueryParam(hidePotentiallyRemovedListingsQueryParamKey, BooleanParam);

  const [isAssetExclusiveParam, setIsAssetExclusiveParam] = useQueryParam(
    isAssetExclusiveQueryParamKey,
    BooleanParam
  );

  const [onlyPriceChangedParam, setOnlyPriceReducedParam] = useQueryParam(
    onlyPriceChangedQueryParamKey,
    BooleanParam
  );

  const [searchState, setSearchState] = useState<SearchState>(
    getSearchStatefromQueryParams(
      ContactParam ?? null,
      searchParam ?? "",
      locationsParam as ProcessedListingAddress[],
      listingOriginsParam as ListingOrigin[],
      minRoomsParam ?? null,
      maxRoomsParam ?? null,
      minPriceParam ?? null,
      maxPriceParam ?? null,
      minParkingSpotsParam ?? null,
      maxParkingSpotsParam ?? null,
      agencyNamesParam as string[],
      sellerNamesParam as string[],
      propertyTypesParam as string[],
      adTypesParam as string[],
      booleanPropertiesDetailsParam as BooleanPropertiesDetails,
      minFloorsParam ?? null,
      maxFloorsParam ?? null,
      hidePotentiallyRemovedListingsParam ?? false,
      isAssetExclusiveParam ?? false,
      onlyPriceChangedParam ?? false,
      listingListedForParam as string,
      minSquareMeterParam ?? null,
      maxSquareMeterParam ?? null
    )
  );

  // Giving a few seconds for the bootstrap to finish
  useEffect(() => {
    if (!contactNumberParam) {
      return;
    }
    setIsBootstrapInProgress(true);
    setTimeout(() => {
      setIsBootstrapInProgress(false);
    }, 3000);
  }, [contactNumberParam]);

  // The picked contact, LIVE — through the same hook every card uses (`useLiveContact`
  // decides which store answers; see `contactDocumentSource.ts`). A document that is gone
  // keeps a `{ id }` stub, so the search stays addressed to the contact; the deep-equal
  // guard inside `updatePickedContact` is what stops the loop.
  const pickedContactId = searchState?.pickedContact?.id ?? null;
  const livePickedContact = useLiveContact(user?.email ? pickedContactId : null);
  useEffect(() => {
    if (!user?.email || !pickedContactId || livePickedContact.loading || livePickedContact.error) {
      return;
    }
    updatePickedContact(
      livePickedContact.contact ?? ({ id: pickedContactId } as RealEstateContact),
      "live"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.email,
    pickedContactId,
    livePickedContact.contact,
    livePickedContact.loading,
    livePickedContact.error
  ]);

  useEffect(() => {
    if (!user || !contactNumberParam) return;

    const LoadSearchStateFromContactNumber = async () => {
      if (contactNumberParam) {
        console.log("Loading search state from contact number", contactNumberParam);

        try {
          setIsLoadingContact(true);
          const contact = await fetchContact(contactsGet, user, contactNumberParam);
          console.log("Contact loaded", contact);
          updatePickedContact(contact, "contact_number");
        } catch (error) {
          console.error("Error loading contact from contact number", error);
        } finally {
          setIsLoadingContact(false);
        }
      }
    };

    LoadSearchStateFromContactNumber();
  }, [user?.mail, contactNumberParam]);

  const [draftSearchState, setDraftSearchState] = useState<SearchState>(defaultSearchState); // Will have the initial updated state in the useEffect below

  // Update draft search state from search state when the state changes
  useEffect(() => {
    console.debug("Updating draft search state from search state with: ", searchState);
    setDraftSearchState(searchState);
  }, [searchState]);

  const { propertyTypes: availablePropertyTypes } = usePropertyTypes();

  const { agencyNames: availableAgencyNames } = useAgencyNames();
  const { sellerNames: availableSellerNames } = useSellerNames();

  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isSearchInProgress, setIsSearchInProgress] = useState<boolean>(false);
  const [isLoadingContact, setIsLoadingContact] = useState<boolean>(false);
  const [isLoadingFavoriteContact, setIsLoadingFavoriteContact] = useState<boolean>(false);
  const [isBootstrapInProgress, setIsBootstrapInProgress] = useState<boolean>(false);
  const [hits, setHits] = useState<AlgoliaHit<ProcessedListingData>[]>([]);
  const [blacklistedListings, setBlacklistedListings] = useState<ListingsRealtorBlacklist>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalHits, setTotalHits] = useState(0);
  const latestFetchDateRef = useRef<Date>();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [favoriteListingsOfPickedContact, setFavoriteListingsOfPickedContact] =
    useState<FavoriteListingsOfContact>(undefined);

  const [favoriteUserListingsOfPickedContact, setFavoriteUserListingsOfPickedContact] = useState<
    FavoriteListingsOfContact[]
  >([]);

  const [listingslists, setLists] = useState<ListingsRealtorSavedList[]>([]);
  const [listIdToListingsData, setListIdToListingsData] = useState<
    Record<string, AlgoliaHit<ProcessedListingData>[]>
  >({});

  useEffect(() => {
    if (!user?.email) {
      setLists([]);
      return;
    }

    const unsubscribe = getUserLists(user.email).onSnapshot((snapshot) => {
      const listsData = snapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data()
          } as ListingsRealtorSavedList)
      );
      setLists(listsData);
    });

    return unsubscribe;
  }, [user?.email]);

  useEffect(() => {
    const allListingIds = listingslists?.length
      ? Array.from(new Set(listingslists.flatMap((list) => list.listingIds || [])))
      : [];

    const abortController = new AbortController();

    const fetchAllListingsInLists = async () => {
      try {
        const allListings = await fetchListingsByIds(allListingIds);

        if (abortController.signal.aborted) return;

        // Map: listId -> listings for that list
        const newMap: Record<string, AlgoliaHit<ProcessedListingData>[]> = {};
        listingslists.forEach((list) => {
          newMap[list.id] = allListings.filter((l) => list.listingIds.includes(l.objectID));
        });
        setListIdToListingsData(newMap);
      } catch (e) {
        if (!abortController.signal.aborted) {
          console.error("Error fetching listings for lists:", e);
          setListIdToListingsData({});
        }
      }
    };

    fetchAllListingsInLists();

    return () => {
      abortController.abort();
    };
  }, [listingslists]);

  useEffect(() => {
    if (!user?.email || !searchState?.pickedContact || !searchState?.pickedContact?.id) {
      setFavoriteListingsOfPickedContact(undefined);
      return;
    }

    setIsLoadingFavoriteContact(true);

    const createFavoriteIfNotExistsAsync = async () => {
      await createFavoriteIfNotExists(user.email, searchState.pickedContact.id);
      setIsLoadingFavoriteContact(false);
    };

    createFavoriteIfNotExistsAsync();
    const query = getFavoritesListingsQuery(user.email, searchState.pickedContact.id);

    return query.onSnapshot((snapshot) => {
      const favorites = pickMinisitePage(snapshot.docs)?.data() ?? null;
      console.log("Favorite listings of picked contact snapshot", favorites);
      setFavoriteListingsOfPickedContact(favorites);
    });
  }, [searchState?.pickedContact, user?.email]);

  useEffect(() => {
    if (!user?.email) return;

    const query = getUserFavoritesListingsQuery(user.email);

    return query.onSnapshot((snapshot) => {
      const favorites = snapshot.docs.map((doc) => doc.data());
      console.log("Favorite user listings of picked contact snapshot", favorites);
      setFavoriteUserListingsOfPickedContact(favorites);
    });
  }, [user?.email]);

  useEffect(() => {
    if (!user?.email) {
      return;
    }

    return getRealtorListingsBlacklistQuery(user.email).onSnapshot((snapshot) => {
      const blacklistedListings = snapshot.data();
      console.log("Blacklisted listings snapshot", blacklistedListings);
      setBlacklistedListings(blacklistedListings);
    });
  }, [user?.email]);

  const addListingToContactsFavorites = async (listingId: string) => {
    console.log("About to add listing to favorites", listingId);

    await addListingToFavorites(favoriteListingsOfPickedContact.path, listingId);
  };

  const addListingsToContactsFavorites = async (listingIds: string[]) => {
    console.log("About to add listings to favorites", listingIds);

    await addListingsToFavorites(favoriteListingsOfPickedContact.path, listingIds);
  };

  const removeListingFromContactsFavorites = async (listingId: string) => {
    await removeListingFromFavorites(favoriteListingsOfPickedContact?.path, listingId);
  };

  const removeListingsFromContactsFavorites = async (listingIds: string[]) => {
    await removeListingsFromFavorites(favoriteListingsOfPickedContact?.path, listingIds);
  };

  const addListingsListToContactsFavorites = async (list: ListingsRealtorSavedList) => {
    console.log("About to add a listings list to favorites", list.id);

    await addListingsListToFavorites(favoriteListingsOfPickedContact.path, list);
  };

  const removeListingsListFromContactsFavorites = async (list: ListingsRealtorSavedList) => {
    console.log("About to remove a listings list from favorites", list.id);

    await removeListingsListFromFavorites(favoriteListingsOfPickedContact?.path, list);
  };

  const addListingToBlackList = async (realtorId: string, listingId: string) => {
    await addListingToRealtorBlackList(realtorId, listingId);
  };

  const removeListingFromBlackList = async (realtorId: string, listingId: string) => {
    await removeListingFromRealtorBlackList(realtorId, listingId);
  };

  const addListingsList = async (name: string) => {
    if (!user?.email) {
      return;
    }

    await createUserList(name, user.email);
  };

  const deleteListingsList = async (listId: string) => {
    await deleteUserList(listId);
  };

  const editListingsListName = async (listId: string, name: string) => {
    await editUserListName(listId, name);
  };

  const addListingsToListingsList = async (listId: string, listingIds: string[]) => {
    await addListingsToUserList(listId, listingIds);
  };

  const removeListingFromListingsList = async (listId: string, listingId: string) => {
    await removeListingFromUserList(listId, listingId);
  };

  // Update query params when search state changes
  useEffect(() => {
    console.log("Updating query params with", searchState);
    setContactNumberParam(null); // nullify the contact number param, as it is not needed when the contact is set
    setContactParam(searchState.pickedContact);
    setSearchParam(undefinedIfStringEmpty(searchState.searchTerm));
    setLocationsParam(undefinedIfArrayEmpty(searchState.locations));
    setListingOriginsParam(searchState.listingOrigins);
    setMinRoomsParam(undefinedIfStringEmpty(searchState.minRooms?.toString()));
    setMaxRoomsParam(undefinedIfStringEmpty(searchState.maxRooms?.toString()));
    setMinPriceParam(undefinedIfNumberEmpty(searchState.minPrice));
    setMaxPriceParam(undefinedIfNumberEmpty(searchState.maxPrice));
    setMinParkingSpotsParam(undefinedIfStringEmpty(searchState.minParkingSpots?.toString()));
    setMaxParkingSpotsParam(undefinedIfStringEmpty(searchState.maxParkingSpots?.toString()));
    setPropertyTypesParam(undefinedIfArrayEmpty(searchState.propertyTypes));
    setAdTypesParam(undefinedIfArrayEmpty(searchState.adTypes));
    setListingListedForParam(searchState.listingListedFor);
    setBooleanPropertiesDetailsParam(searchState.booleanPropertiesDetails ?? {});
    setMinFloorsParam(undefinedIfStringEmpty(searchState.minFloors?.toString()));
    setMaxFloorsParam(undefinedIfStringEmpty(searchState.maxFloors?.toString()));
    setMinSquareMeterParam(undefinedIfNumberEmpty(searchState.minSquareMeter));
    setMaxSquareMeterParam(undefinedIfNumberEmpty(searchState.maxSquareMeter));
    setHidePotentiallyRemovedListingsParam(
      undefinedIfBooleanEmpty(searchState.hidePotentiallyRemovedListings)
    );
    setIsAssetExclusiveParam(undefinedIfBooleanEmpty(searchState.isAssetExclusive));
    setOnlyPriceReducedParam(undefinedIfBooleanEmpty(searchState.onlyPriceChanged));
  }, [searchState]);

  const fetchHits = async () => {
    let healthId = 0;
    try {
      setIsSearchInProgress(true);
      console.log("Fetching hits...");
      const dateNow = new Date();
      latestFetchDateRef.current = dateNow;

      console.log(
        `Fetching listings on page ${currentPage} for search term ${
          searchState.searchTerm
        } and locations ${JSON.stringify(searchState.locations)}...`
      );

      // Postgres search needs no Algolia index — only wait for one when the
      // search is actually going to be issued against Algolia.
      if (!isPgSearchEnabled() && !algoliaIndex) {
        console.log("Algolia index not yet ready - skipping fetch");
        setIsSearchInProgress(false);
        return;
      }
      healthId = healthSearchStarted({
        page: currentPage,
        locations: searchState.locations?.length ?? 0,
        hasContact: !!searchState.pickedContact
      });
      const result = await fetchListings(algoliaIndex, searchState, 300, currentPage);

      if (latestFetchDateRef.current && latestFetchDateRef.current > dateNow) {
        console.log("Fetched data - Ignoring stale fetch results");
        healthSearchEnded(healthId, { stale: true, hits: result.hits.length });
        return;
      }
      healthSearchEnded(healthId, { hits: result.hits.length, total: result.totalHits ?? null });

      console.log(`Settings ${result.hits.length} hits. Total hits available: ${result.totalHits}`);
      setHits(result.hits);
      setTotalHits(result.totalHits);
      setIsSearchInProgress(false); // not in finally clause, as we want to keep the spinner until the new data is set, and the try catch might be called several times, and finally will be called, ending the loading
    } catch (error) {
      console.log("Error fetching hits", error);
      healthSearchEnded(healthId, {
        error: (error instanceof Error ? error.message : String(error)).slice(0, 200)
      });
      setIsSearchInProgress(false);
    }
  };

  const updatePickedContact = (
    contact: RealEstateContact | undefined,
    source: "picker" | "live" | "contact_number" = "picker"
  ) => {
    console.log("Updating picked contact with", contact);

    // compare with lodash to avoid infinite loop
    if (_.isEqual(contact, searchState?.pickedContact)) {
      healthCrumb("contact_unchanged", { source });
      console.log(
        "The contact is the same as the current picked contact",
        contact,
        searchState?.pickedContact
      );
      return;
    }
    // Which fields differ, by name: a live copy that never equals the picked one re-runs the search.
    healthCrumb("contact_picked", {
      source,
      hasContact: !!contact,
      sameId: !!contact && contact.id === searchState?.pickedContact?.id,
      diff: changedKeys(contact, searchState?.pickedContact)
    });
    if (!contact) {
      // setSearchState((s) => ({ ...s, pickedContact: null }));
      setSearchState(defaultSearchState);
    } else {
      const newSearchState = getNewStateSearchFromContactFields(contact);
      console.debug("New search state from contact", newSearchState);
      console.debug("Old search state", searchState);
      setSearchState(newSearchState);
    }
  };

  const updateSearchState = (newSearchStatePartial: Partial<SearchState>) => {
    console.log("Updating search state with", newSearchStatePartial);
    if (!newSearchStatePartial) {
      setSearchState(defaultSearchState);
    }
    const newSearchState = { ...searchState, ...newSearchStatePartial };
    healthCrumb("search_state", { changed: changedKeys(newSearchState, searchState) });

    setSearchState(newSearchState);

    console.log("New search state", newSearchState);
    console.log("Old search state", searchState);

    if (
      !_.isEqual(newSearchState, defaultSearchState) &&
      !_.isEqual(newSearchState, searchState) &&
      isSearchStateDirty(newSearchState)
    ) {
      console.log("setting isSearchInProgress to true");
      setIsSearchInProgress(true);
    }
  };

  useEffect(() => {
    if (isLoadingOrganization) {
      console.log("Still loading organizations, skipping fetchHits");
      healthCrumb("search_skipped", { reason: "organizations_loading" });
      return;
    }
    if (!user) {
      console.log("Still loading user, skipping fetchHits");
      healthCrumb("search_skipped", { reason: "no_user" });
      return;
    }

    // Only trigger search if the search state has meaningful criteria (is "dirty")
    if (!isSearchStateDirty(searchState)) {
      console.log("Search state is not dirty, skipping fetchHits");
      healthCrumb("search_skipped", { reason: "not_dirty" });
      return;
    }

    console.log("Triggering fetchHits...");
    fetchHits();
    posthog?.capture("search_listings_match", {
      city: searchState.locations?.length > 0 ? searchState.locations[0].city : undefined
    });
    // NOTE: `organizations` is intentionally NOT a dependency — fetchHits doesn't
    // read it (the org tab filters already-fetched hits client-side), and the
    // org list arrives as a fresh array reference, which used to re-run the
    // whole search for nothing. `isLoadingOrganization` already gates the
    // initial wait until orgs have loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchState, user?.email, isLoadingOrganization]);

  useEffect(() => {
    if (isSearchStateDirty(searchState)) {
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }
  }, [searchState]);

  const undefinedIfArrayEmpty = (arr: any[]) => (arr?.length > 0 ? arr : undefined);

  const undefinedIfStringEmpty = (str: string) => {
    const trimmed = _.trim(str);
    return _.isEmpty(trimmed) ? undefined : trimmed;
  };

  const undefinedIfNumberEmpty = (str: number) => {
    return isNaN(str as number) ? undefined : str;
  };

  const undefinedIfBooleanEmpty = (bool: boolean) => {
    return bool === false ? undefined : bool;
  };

  const saveSearchStateToPickedContact = async () => {
    if (!searchState.pickedContact) {
      return;
    }
    const contact = searchState.pickedContact;
    enqueueSnackbar(t("listingsMatch.savingContact"), { variant: "info" });

    console.log("Saving search state to contact", contact?.id);
    const {
      locations,
      maxPrice,
      minPrice,
      propertyTypes,
      minRooms,
      maxRooms,
      minFloors,
      maxFloors,
      minSquareMeter,
      maxSquareMeter
    } = draftSearchState; // using the draft search state, because users might do some changes without searching, and expect the changes to be saved

    // Only the amenity keys a contact carries: the server validates the patch against a
    // whitelist and refuses the WHOLE save over one key it does not know.
    const additionalFiltersTrueOnly = _.pick(
      _.pickBy(draftSearchState.booleanPropertiesDetails, (v) => !!v),
      genericPropertyDetailsKeys
    );

    // Budget writes preserve the ACTUAL searched range: the stored budget field is
    // semantically the MAX and the explicit lower bound rides customerBudget(Rent)Min —
    // the same semantics the contact dialog's range mode and the contact page's
    // Matches tab read and write. (The old code collapsed min+max to their midpoint,
    // so saving a 1-2M search stamped the contact with a 1.5M "budget".)
    // A rent search writes the rent field pair; the sale pair is left untouched.
    const isRentSearch = draftSearchState.listingListedFor === "rent";
    let budgetMax = isRentSearch
      ? searchState.pickedContact.customerBudgetRent
      : searchState.pickedContact.customerBudget;
    // undefined = leave the stored lower bound untouched; null = clear it (a search
    // with no explicit lower bound must not keep a stale one under the new max).
    // `!= null` throughout: a lower bound of 0 ("everything under the max") is a real
    // bound the realtor set on the slider. Read as truthy/falsy it fell through to the
    // max-only branch and stamped the contact with `customerBudgetMin: null`, i.e. a
    // lone budget — which every seeding path then re-reads as "±10% around 1.5M",
    // moving the ceiling the realtor had just saved.
    let budgetMin: number | null | undefined;
    if (minPrice != null && maxPrice != null) {
      budgetMax = Math.round(maxPrice);
      budgetMin = Math.round(minPrice);
    } else if (minPrice != null) {
      budgetMax = Math.round(minPrice);
      budgetMin = null;
    } else if (maxPrice != null) {
      budgetMax = Math.round(maxPrice);
      budgetMin = null;
    }

    let updates: Partial<RealEstateContact> = {
      roomsMax: maxRooms,
      roomsMin: minRooms,
      floorsMax: maxFloors,
      floorsMin: minFloors,
      // Like rooms and floors here: a bound that is SET is written, an empty one leaves the stored
      // value alone (the nil sweep below) — this action records a search, it does not clear a card.
      squareMeterMin: minSquareMeter,
      squareMeterMax: maxSquareMeter,
      ...(isRentSearch ? { customerBudgetRent: budgetMax } : { customerBudget: budgetMax }),
      // Stored WITHOUT the location-group stamp. Provenance describes how an area
      // entered a live selection, not what the contact is interested in, and the
      // server's own write path (`asInterestedInLocations` in contactWriteRules)
      // rebuilds each entry from named fields and drops it anyway — so a client
      // write that kept it would make the same contact render as one group chip
      // or as twelve plain ones depending on which surface last saved it.
      interestedInLocations: toPersistedLocations(locations),
      interestedInPropertyTypes: propertyTypes,
      ...additionalFiltersTrueOnly
    };

    // (Was: a second write of the same fact into the deprecated free-text
    // `neighborhood` — `locations.map((l) => l.neighborhood).join(", ")`, city dropped.
    // This was the ONE remaining writer of that field in either repo; the server's
    // contact endpoint has refused it for a while precisely because nothing reconciles
    // the two representations. `interestedInLocations` above is the fact.)

    updates = _.omitBy(updates, _.isNil);
    // The lower bound is the one field where null is a WRITE (clear the stored
    // bound), not "leave untouched" — attach it after the nil sweep.
    if (budgetMin !== undefined) {
      if (isRentSearch) updates.customerBudgetRentMin = budgetMin as number;
      else updates.customerBudgetMin = budgetMin as number;
    }

    console.log("Updates to contact", updates);

    try {
      // Edit the contact, or create it: the search can be run for a phone that was typed
      // in and is not a contact yet. The server decides which — this context has never
      // held the book, and the browser's own upsert guessed "new" every time.
      await saveContact({
        contactId: contact.id,
        fields: updates,
        fieldsOnCreate: { contactOrigin: SYSTEM_CONTACT_ORIGINS.dashboardSearch }
      });
      enqueueSnackbar(t("listingsMatch.savedContact"), { variant: "success" });
    } catch (error) {
      console.error("Error saving contact", error);
      enqueueSnackbar(t("listingsMatch.errorSavingContact"), { variant: "error" });
    }
  };

  const value: ListingsMatchContextType = {
    searchState: searchState,
    draftSearchState: draftSearchState,
    setDraftSearchState: (s) => {
      const next = { ...draftSearchState, ...s };
      healthCrumb("draft", { changed: changedKeys(next, draftSearchState) });
      setDraftSearchState(next);
    },
    updateSearchState: updateSearchState,
    updatePickedContact: updatePickedContact,
    applyDraftSearchState: () => {
      healthCrumb("apply_draft");
      updateSearchState(draftSearchState);
    },
    clearSearchState: () => {
      console.log("Clearing search state to default");
      healthCrumb("clear");
      setSearchState(defaultSearchState);
    },
    clearDraftSearchState: () => {
      console.log("Clearing draft search state to default");
      console.log("Draft search state", draftSearchState);
      console.log("Default search state", defaultSearchState);
      setDraftSearchState(defaultSearchState);
    },
    availablePropertyTypes,
    availableAgencyNames,
    availableSellerNames,
    isSearchInProgress: isSearchInProgress || isLoadingContact,
    isBootstrapInProgress,
    isDirty,
    hits,
    totalHits,
    favoriteListingsOfPickedContact,
    favoriteUserListingsOfPickedContact,
    blacklistedListings,
    listingslists,
    listIdToListingsData,
    saveSearchStateToPickedContact,
    addListingToContactsFavorites,
    addListingsToContactsFavorites,
    removeListingFromContactsFavorites,
    removeListingsFromContactsFavorites,
    addListingsListToContactsFavorites,
    removeListingsListFromContactsFavorites,
    addListingToBlackList,
    removeListingFromBlackList,
    addListingsList,
    deleteListingsList,
    editListingsListName,
    addListingsToListingsList,
    removeListingFromListingsList
  };

  return <ListingsMatchContext.Provider value={value}>{children}</ListingsMatchContext.Provider>;
}

export function useListingsMatchContext() {
  const context = useContext(ListingsMatchContext);

  if (!context)
    throw new Error("useListingsMatchContext must be used within a ListingsMatchContextProvider");

  return context;
}
